import classNames from 'classnames';
import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import styles from './DonePopup.module.scss';
import { iRootDispatch, iRootState } from '../../../../store/store';
import Popup from 'devextreme-react/popup';
import Button from 'fulogics-ui-web/dist/layout/dx/Button/Button';
import {
    donePopupCloseDraftButtonId,
    donePopupCloseDraftDateBoxId,
    donePopupCloseImmediatelyButtonId,
    donePopupValidationGroup,
} from './inspectorOptions';
import DateBox from 'fulogics-ui-web/dist/layout/dx/DateBox/DateBox';
import moment from 'moment';
import { sendOrderPayload } from '../../../../store/models/COSOrdersModel';
import toast from 'fulogics-ui-web/dist/utils/toast';
import { OrderOrderPopupIcon } from '../../../assets/svg/menu/OrderOrderPopupIcon';
import { DraftOrderPopupIcon } from '../../../assets/svg/menu/DraftOrderPopupIcon';
import { custom } from 'devextreme/ui/dialog';
import i18n from '../../../../translations/i18n';
import CheckBox from 'fulogics-ui-web/dist/layout/dx/CheckBox/CheckBox';
import { useMaxSendDate } from '../../../hooks/useMaxSendDate';
import Validator, { RangeRule } from 'devextreme-react/validator';
import ValidationSummary from 'devextreme-react/validation-summary';

const mapStateToProps = (state: iRootState) => ({} as DonePopupDisplayOptions);

const mapDispatch = (_dispatch: iRootDispatch, _ownDispatch: DonePopupDisplayOptions) =>
    ({
        sendOrder: _dispatch.COSOrders.sendOrder,
        //
        ..._ownDispatch,
    } as DonePopupDisplayOptions);

const DonePopup = connect(
    mapStateToProps,
    mapDispatch as any
)((props: DonePopupProps) => {
    const { t } = useTranslation('OrderEditScreenDonePopup');
    const { maxSendDate, loadingMaxSendDate, validationAdapter } = useMaxSendDate(
        props.data?.forwardSteps?.id,
        props.data?.returnSteps?.id
    );
    const [isSendDateValid, setIsSendDateValid] = useState(true);
    const [draftCloseDeadline, setDraftCloseDeadline] = useState<Date>(
        moment().subtract(5, 'hours').toDate()
    );

    const [isDateBoxVisible, setDateboxVisible] = useState<boolean>(true);

    const onCloseImmediatelyClickedDialog = () => {
        return custom({
            messageHtml: `<div class="confirmCustomDialogContainer">
        <div class="confirmCustomDialogContainerTop">${t(
            'sendOrder'
        )}</div><div class="confirmCustomDialogContainerBottom">${t(
                'confirmSendOrder'
            )}</div></div>`,
            showTitle: false,
            buttons: [
                {
                    // text: "t~OrderNewScreenDonePopup:confirmOrder",
                    text: i18n.t('OrderEditScreenDonePopup:cancel'),
                    type: 'danger',
                    stylingMode: 'contained',
                    onClick: async (e) => {
                        props.onDonePopupStatusChange?.();
                    },
                },
                {
                    stylingMode: 'contained',
                    type: 'success',
                    // text: "t~OrderNewScreenDonePopup:cancel",
                    text: i18n.t('OrderEditScreenDonePopup:confirm'),
                    onClick: (e) => {
                        props
                            .sendOrder?.({
                                id: props.data?.id ?? '-1',
                                asDraft: false,
                            })
                            .then(() => props.onDone?.())
                            .catch((error) => {
                                const title =
                                    error?.detail ||
                                    i18n.t('OrderEditScreenDonePopup:invalidOrder');

                                toast.exception(error, title);
                            });
                    },
                },
            ],
        });
    };

    const onCloseDraftClickedDialog = () => {
        return custom({
            messageHtml: `<div className={${classNames(styles.confirmContainer)}}>
        <div className={${classNames(styles.confirmContainerTop)}}>${t(
                'sendDraft'
            )}</div><div className={${classNames(styles.confirmContainerBottom)}}>${t(
                'confirmSendDraft'
            )}</div></div>`,
            showTitle: false,
            buttons: [
                {
                    // text: "t~OrderNewScreenDonePopup:confirmOrder",
                    text: i18n.t('OrderEditScreenDonePopup:cancel'),
                    type: 'danger',
                    stylingMode: 'contained',
                    onClick: async (e) => {
                        props.onDonePopupStatusChange?.();
                    },
                },
                {
                    stylingMode: 'contained',
                    type: 'success',
                    text: i18n.t('OrderEditScreenDonePopup:confirmDraft'),
                    onClick: (e) => {
                        props
                            .sendOrder?.({
                                id: props.data?.id ?? '-1',
                                asDraft: true,
                                draftCloseDeadline: isDateBoxVisible
                                    ? draftCloseDeadline
                                    : undefined,
                            })
                            .then(() => props.onDone?.())
                            .catch((error) => toast.exception(error));
                    },
                },
            ],
        });
    };

    const onCloseImmediatelyClicked = useCallback(() => {
        props.onDonePopupStatusChange?.()?.then(onCloseImmediatelyClickedDialog().show());
    }, [props.data, props.sendOrder, props.onDone, props.isVisible]);

    const onCloseDraftClicked = useCallback(() => {
        props.onDonePopupStatusChange?.()?.then(onCloseDraftClickedDialog().show());
    }, [
        props.data,
        props.sendOrder,
        props.onDone,
        props.isVisible,
        isDateBoxVisible,
        draftCloseDeadline,
    ]);

    const onCheckBoxValueChanged = () => {
        setDateboxVisible((visible) => !visible);
    };

    useEffect(() => {
        if (maxSendDate) {
            setDraftCloseDeadline(maxSendDate);
        }
    }, [maxSendDate]);

    const deadlineErrorInfo = {
        message: t('maxDataError'),
    };

    return (
        <Popup
            visible={props.isVisible}
            dragEnabled={false}
            closeOnOutsideClick={true}
            showCloseButton={false}
            showTitle
            width={600}
            height="auto"
            title={t('header')}
            className={classNames(styles.container, props.className)}
            onHiding={props.onDonePopupStatusChange}
        >
            <div>
                <ValidationSummary validationGroup={donePopupValidationGroup} />
                <Validator validationGroup={donePopupValidationGroup} adapter={validationAdapter}>
                    <RangeRule max={maxSendDate} message={t('maxDataError')} />
                </Validator>
                <div className={classNames(styles.topMiddle)}>
                    <div className={classNames(styles.topDonePopupIcon)}>
                        <OrderOrderPopupIcon />
                    </div>
                    <div className={classNames(styles.topMiddleDescription)}>
                        <label className={classNames(styles.topMiddleDescriptionTitle)}>
                            {t('topMiddleDescriptionTitle')}
                        </label>
                        <label className={classNames(styles.topMiddleDescriptionText)}>
                            {t('topMiddleDescriptionText')}
                        </label>
                    </div>
                    <div className={classNames(styles.donePopupCloseImmediatelyButton)}>
                        <Button
                            inspectorId={donePopupCloseImmediatelyButtonId}
                            onClick={onCloseImmediatelyClicked}
                            disabled={loadingMaxSendDate}
                        />
                    </div>
                </div>
                <hr />
                <div className={classNames(styles.bottomMiddle)}>
                    <div className={classNames(styles.bottomMiddleTop)}>
                        <div className={classNames(styles.topDonePopupIcon)}>
                            <DraftOrderPopupIcon />
                        </div>
                        <div className={classNames(styles.bottomMiddleDescription)}>
                            <label className={classNames(styles.bottomMiddleDescriptionTitle)}>
                                {t('bottomMiddleDescriptionTitle')}
                            </label>
                            <label className={classNames(styles.bottomMiddleDescriptionText)}>
                                {t('bottomMiddleDescriptionText')}
                            </label>
                        </div>
                    </div>

                    <label className={classNames(styles.dateAndTimeClosingDraft)}>
                        {' '}
                        {t('dateAndTimeClosingDraft')}
                    </label>
                    <div className={classNames(styles.closDraftDateBox)}>
                        <DateBox
                            disabled={!isDateBoxVisible || loadingMaxSendDate}
                            value={draftCloseDeadline}
                            onValueChanged={(e) => setDraftCloseDeadline(e.value)}
                            inspectorId={donePopupCloseDraftDateBoxId}
                            validationError={deadlineErrorInfo}
                            min={new Date()}
                        >
                            <Validator onValidated={(x) => setIsSendDateValid(!!x.isValid)}>
                                <RangeRule max={maxSendDate} message={t('maxDataError')} />
                            </Validator>
                        </DateBox>
                    </div>
                    <div className={classNames(styles.donePopupCloseDraftButton)}>
                        <div className={classNames(styles.donePopupCheckBox)}>
                            <CheckBox
                                text={t('donePopupCheckBox')}
                                onValueChanged={onCheckBoxValueChanged}
                                defaultValue={false}
                                disabled={loadingMaxSendDate}
                            />
                        </div>
                        <div>
                            <Button
                                inspectorId={donePopupCloseDraftButtonId}
                                onClick={onCloseDraftClicked}
                                disabled={
                                    loadingMaxSendDate || (isDateBoxVisible && !isSendDateValid)
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Popup>
    );
});

export default DonePopup;

export type DonePopupProps = {
    data?: DonePopupData;
} & DonePopupDisplayOptions;

export interface DonePopupDisplayOptions {
    className?: string;
    //
    isVisible?: boolean;
    //
    sendOrder?: (payload: sendOrderPayload) => Promise<void>;
    onDone?: () => Promise<void>;
    onDonePopupStatusChange?: () => Promise<void>;
}

export type DonePopupData = {
    id?: string;
    forwardSteps?: { id?: string };
    returnSteps?: { id?: string };
};

export const DonePopupData = ['id', 'forwardSteps.id', 'returnSteps.id'];
